import { useDashboardDetailData } from "web_myplann/Dashboard/Hooks/useDashboardDetailData";
import { useDashboardDetailRows } from "web_myplann/Dashboard/Hooks/useDashboardDetailRows";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import type { DashboardDetailRowData } from "web_myplann/Dashboard/Utils/dashboardDetailRows";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

export type DashboardCategorySectionData = {
	overviewType: PortfolioReportingOverviewTypeEnum;
	title: string;
	subtotalText: string;
	rows: DashboardDetailRowData[];
	navigateTo: string;
};

export const useDashboardTotalSections = (dashboards: DashboardType[]) => {
	const { t } = useResource();

	const has = (type: PortfolioReportingOverviewTypeEnum) =>
		dashboards.some((d) => d.overviewType === type);
	const gate = (type: PortfolioReportingOverviewTypeEnum) =>
		has(type) ? type : undefined;

	const investment = useDashboardDetailData(
		gate(PortfolioReportingOverviewTypeEnum.Investment),
	);
	const investmentProperty = useDashboardDetailData(
		gate(PortfolioReportingOverviewTypeEnum.InvestmentProperty),
	);
	const cash = useDashboardDetailData(
		gate(PortfolioReportingOverviewTypeEnum.Cash),
	);
	const liabilities = useDashboardDetailData(
		gate(PortfolioReportingOverviewTypeEnum.Liabilities),
	);
	const other = useDashboardDetailData(
		gate(PortfolioReportingOverviewTypeEnum.Other),
	);

	const investmentRows = useDashboardDetailRows(
		investment.portfolios?.data,
		investment.properties?.data,
		investment.reporting?.data,
	).rows;
	const investmentPropertyRows = useDashboardDetailRows(
		investmentProperty.portfolios?.data,
		investmentProperty.properties?.data,
		investmentProperty.reporting?.data,
	).rows;
	const cashRows = useDashboardDetailRows(
		cash.portfolios?.data,
		cash.properties?.data,
		cash.reporting?.data,
	).rows;
	const liabilitiesRows = useDashboardDetailRows(
		liabilities.portfolios?.data,
		liabilities.properties?.data,
		liabilities.reporting?.data,
	).rows;
	const otherRows = useDashboardDetailRows(
		other.portfolios?.data,
		other.properties?.data,
		other.reporting?.data,
	).rows;

	const rowsByType: Record<string, DashboardDetailRowData[]> = {
		[PortfolioReportingOverviewTypeEnum.Investment]: investmentRows,
		[PortfolioReportingOverviewTypeEnum.InvestmentProperty]:
			investmentPropertyRows,
		[PortfolioReportingOverviewTypeEnum.Cash]: cashRows,
		[PortfolioReportingOverviewTypeEnum.Liabilities]: liabilitiesRows,
		[PortfolioReportingOverviewTypeEnum.Other]: otherRows,
	};

	const sections: DashboardCategorySectionData[] = dashboards
		.filter((d) => d.overviewType !== PortfolioReportingOverviewTypeEnum.Total)
		.map((d) => ({
			overviewType: d.overviewType,
			title: d.type,
			subtotalText: moneyFormatter(
				Math.round(d.value ?? 0),
				getPortfolioCurrencySign(t, d.currency),
			),
			rows: rowsByType[d.overviewType] ?? [],
			navigateTo: Routing.DashboardDetail.path(d.overviewType),
		}));

	const isLoading =
		investment.isAnyLoading ||
		investmentProperty.isAnyLoading ||
		cash.isAnyLoading ||
		liabilities.isAnyLoading ||
		other.isAnyLoading;

	return { sections, isLoading };
};
